import * as queryString from 'query-string'
import { type Location, type NavigateFunction } from 'react-router'
import { type ParsedQuery } from 'query-string'
import {
  TILE_SIZES,
  MATERIAL_TYPES,
  FILTERS,
  FRAME_COLORS,
  MAT_COLORS,
  MATTING,
  TILE_CONTENT,
} from '@mixtiles/web-backend-shared'

import { AiTemplateTypeSchema } from '../types/ai/template-schema'
import { CropAlignmentSchema } from './cropAlignment.consts'
import { PROD_URL } from '../config/config'
import { isClient } from './runtimeUtils'
import { toBrazeExternalId } from '../services/Analytics/platforms/braze.utils'
import { isNonEmptyString } from './stringUtils'

export function getUrlParamsFromRequest(
  request: Request,
  urlParam: string
): string | null {
  const url = new URL(request.url)
  return urlParam ? url.searchParams.get(urlParam) : null
}

export function getUrlParams(): ParsedQuery {
  return queryString.parse(window.location.search)
}

export function getUrlParam(param: string): string | null {
  const result = getUrlParams()[param]
  if (Array.isArray(result)) {
    // if multiple params exist with same key, take only the first one
    return result[0]
  }
  return result
}

// Get URL parameter and remove all spaces and plus signs
// Instagram sometimes adds plus signs (+) in URLs that decode to spaces
export function getCleanUrlParam(param: string): string | null {
  const urlParam = getUrlParam(param)
  if (!urlParam) {
    return null
  }
  return urlParam.replace(/\s+/g, '').replace(/\+/g, '')
}

export const CENTERPIECE_QUERY_PARAM = 'centerpiece_image'
export const CENTERPIECE_QUERY_PARAM_TEXT = 'centerpiece_text'

export const clearCenterpieceQueryParams = (
  navigate: NavigateFunction,
  location: Location
) => {
  // Get current search params
  const searchParams = new URLSearchParams(location.search)

  // Remove centerpiece-related params
  searchParams.delete('centerpiece_image')
  searchParams.delete('centerpiece_text')
  // Update URL without causing navigation (replace: true)
  navigate(
    {
      pathname: location.pathname,
      search: searchParams.toString(),
      hash: location.hash,
    },
    { replace: true, state: { ...location.state } }
  )
}

export function parseCenterpieceQueryParams(location: Location) {
  const queryParams = new URLSearchParams(location.search)
  if (!queryParams.has(CENTERPIECE_QUERY_PARAM)) {
    return { centerpieceImage: null, centerpieceText: null }
  }
  const centerpieceImage = queryParams.get(CENTERPIECE_QUERY_PARAM)
  const centerpieceText = queryParams.get(CENTERPIECE_QUERY_PARAM_TEXT)
  return {
    centerpieceImage,
    centerpieceText,
  }
}

// We enable the user to upload photos to the app by sending them in the URL.
// Params is ?photos=URL1|SIZE1|MATERIAL1|FRAME_COLOR1|MATTING1|MATTING_COLOR1|FILTER1|TILE_CONTENT1|AI_TEMPLATE_CATEGORY1|AI_TEMPLATE_NAME1|AI_TEMPLATE_VARIANT1|AI_TEMPLATE_JOB_ID1|AI_TEMPLATE_RESULT_INDEX1|AI_TEMPLATE_ID1|AI_TEMPLATE_VARIANT_INDEX1|AI_TEMPLATE_TYPE1|INITIAL_CROP_ALIGNMENT1&photos=URL2|...
// Example: http://localhost:3000/photos?photos=https://res.cloudinary.com/mixtiles-test/image/upload/v1711954250/gallery_wall_demo/efo6d6xwhimfsp1n4jrc.png&photos=https://res.cloudinary.com/mixtiles-test/image/upload/v1711954250/gallery_wall_demo/efo6d6xwhimfsp1n4jrc.png|RECTANGLE_12X16|wide_frame|white|deep|LIGHT_RED|noir|AIGenPhoto
const PHOTOS_QUERY_PARAM = 'photos'

// NaN rather than a number would fail the z.number() fields on AiTemplateSchema.
function parseOptionalInt(value: string | undefined): number | undefined {
  const parsed = Number.parseInt(value ?? '', 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

export function parsePhotosQueryParams(
  location: Location,
  navigate: NavigateFunction
) {
  const queryParams = new URLSearchParams(location.search)
  if (!queryParams.has(PHOTOS_QUERY_PARAM)) {
    return []
  }

  const photosToUpload = queryParams
    .getAll(PHOTOS_QUERY_PARAM)
    .map((photo) => {
      const splitParam = photo.split('|')
      const [
        url,
        tileSize,
        material,
        frameColor,
        matting,
        mattingColor,
        filter,
        tileContent,
        aiTemplateCategory,
        aiTemplateName,
        aiTemplateVariant,
        aiTemplateJobId,
        aiTemplateResultIndex,
        aiTemplateId,
        aiTemplateVariantIndex,
        aiTemplateType,
        initialCropAlignment,
      ] = splitParam
      if (url === undefined) {
        return null
      }
      const parsedTemplateType = AiTemplateTypeSchema.safeParse(aiTemplateType)
      const parsedCropAlignment = CropAlignmentSchema.safeParse(
        initialCropAlignment?.toLowerCase()
      )
      return {
        url,
        tileSize:
          tileSize &&
          Object.values(TILE_SIZES).includes(
            tileSize.toUpperCase() as TILE_SIZES
          )
            ? tileSize.toUpperCase()
            : undefined,
        materialType:
          material &&
          Object.values(MATERIAL_TYPES).includes(material as MATERIAL_TYPES)
            ? material
            : undefined,
        customStyle: {
          filter:
            filter &&
            Object.values(FILTERS).includes(filter.toLowerCase() as FILTERS)
              ? filter
              : FILTERS.NONE,
          frameColor:
            frameColor &&
            Object.values(FRAME_COLORS).includes(frameColor as FRAME_COLORS)
              ? frameColor
              : undefined,
          matting:
            matting && Object.values(MATTING).includes(matting as MATTING)
              ? matting
              : undefined,
          mattingColor:
            mattingColor &&
            Object.keys(MAT_COLORS).includes(mattingColor.toUpperCase())
              ? mattingColor.toUpperCase()
              : undefined,
        },
        tileContent:
          tileContent &&
          Object.values(TILE_CONTENT).includes(tileContent as TILE_CONTENT)
            ? (tileContent as TILE_CONTENT)
            : undefined,
        aiTemplate:
          aiTemplateCategory ||
          aiTemplateName ||
          aiTemplateVariant ||
          aiTemplateJobId
            ? {
                templateCategory: aiTemplateCategory || undefined,
                templateName: aiTemplateName || undefined,
                variant: aiTemplateVariant || undefined,
                jobId: aiTemplateJobId || undefined,
                resultIndex: parseOptionalInt(aiTemplateResultIndex),
                templateId: aiTemplateId || undefined,
                generationVariantIndex: parseOptionalInt(
                  aiTemplateVariantIndex
                ),
                templateType: parsedTemplateType.success
                  ? parsedTemplateType.data
                  : undefined,
              }
            : undefined,
        initialCropAlignment: parsedCropAlignment.success
          ? parsedCropAlignment.data
          : undefined,
      }
    })
    .filter((photo) => !!photo)
  queryParams.delete(PHOTOS_QUERY_PARAM)
  navigate(
    {
      search: queryParams.toString(),
    },
    { replace: true }
  )
  return photosToUpload
}

export function parsePathSegments(pathname: string): string[] {
  if (!isNonEmptyString(pathname)) {
    return []
  }

  return pathname.split('/').filter((segment) => segment.length > 0)
}

export function getOrigin(): string {
  // used because we also have mixtiles.de. fallback to PROD_URL
  return isClient() && window.location.origin
    ? window.location.origin
    : PROD_URL
}

export function getUrlWithProtocol(request: Request): string {
  const url = new URL(request.url)

  // Check for X-Forwarded-Proto header to respect the original protocol when behind a proxy
  const forwardedProto = request.headers.get('x-forwarded-proto')

  if (forwardedProto) {
    url.protocol = `${forwardedProto}:`
  }

  return url.toString()
}

const MIXTILES_DOMAIN = 'mixtiles.com'
const MIXTILES_SUBDOMAIN_SUFFIX = `.${MIXTILES_DOMAIN}`

export function isAllowedExternalRedirect(url: string): boolean {
  try {
    const { hostname, protocol } = new URL(url)
    if (protocol !== 'https:' && protocol !== 'http:') {
      return false
    }
    return (
      hostname === MIXTILES_DOMAIN ||
      hostname.endsWith(MIXTILES_SUBDOMAIN_SUFFIX)
    )
  } catch {
    return false
  }
}

export function getMixtilesSubdomain(url: URL): string | undefined {
  if (!url.hostname.endsWith(MIXTILES_SUBDOMAIN_SUFFIX)) {
    return undefined
  }

  return url.hostname
    .slice(0, -MIXTILES_SUBDOMAIN_SUFFIX.length)
    .split('.')
    .pop()
}

function mergeMissingParams(
  input: URLSearchParams,
  target: URLSearchParams
): void {
  input.forEach((value, key) => {
    if (!target.has(key)) target.set(key, value)
  })
}

export const BRAZE_EXTERNAL_ID_PARAM = 'initial_braze_id'

export function buildRedirectUrl(
  redirectTo: string,
  forwardedParams: string,
  capturedEmail?: string | null
): string {
  if (!URL.canParse(redirectTo)) {
    throw new Error(
      `buildRedirectUrl expects an absolute URL, got: ${redirectTo}`
    )
  }
  const dest = new URL(redirectTo)
  const incomingParams = new URLSearchParams(forwardedParams)
  incomingParams.delete('redirectTo')
  mergeMissingParams(incomingParams, dest.searchParams)

  if (
    isNonEmptyString(capturedEmail) &&
    !dest.searchParams.has(BRAZE_EXTERNAL_ID_PARAM)
  ) {
    dest.searchParams.set(
      BRAZE_EXTERNAL_ID_PARAM,
      toBrazeExternalId(capturedEmail)
    )
  }

  return dest.toString()
}
